/**
 * Conversion between the columnar data format used by MultiTableGrid
 * and the GridData format used by the edit propagation engine.
 */

import type {
  DerivedMetricDef,
  EditConfig,
  GridData,
  KpiFlow,
  MetricValues,
  TemporalAgg
} from "./editPropagation";
import { deriveTtlFromPtdPtg, recalcDerivedMetrics } from "./editPropagation";

const COLUMN_SEPARATOR = "|";
const TEMPORAL_AGGS: ReadonlySet<string> = new Set(["PTD", "PTG", "TTL"]);

/**
 * Parses a column identifier like "GS1|PTD" into its metric and
 * temporal aggregation parts.
 */
export function parseColumnId(column: string): {
  metric: string;
  agg: TemporalAgg;
} {
  const idx = column.lastIndexOf(COLUMN_SEPARATOR);
  if (idx === -1) {
    throw new Error(
      `Invalid column id: "${column}" (missing "${COLUMN_SEPARATOR}")`
    );
  }
  const metric = column.slice(0, idx);
  const agg = column.slice(idx + 1);
  if (!TEMPORAL_AGGS.has(agg)) {
    throw new Error(`Invalid temporal agg "${agg}" in column "${column}"`);
  }
  return { metric, agg: agg as TemporalAgg };
}

/** Builds a column identifier from a metric name and temporal aggregation. */
export function buildColumnId(metric: string, agg: TemporalAgg): string {
  return `${metric}${COLUMN_SEPARATOR}${agg}`;
}

/**
 * Converts the columnar format used by MultiTableGrid into the GridData
 * format expected by the edit propagation engine.
 *
 * - Only PTD and PTG columns are extracted; TTL columns are ignored
 *   (TTL is always derived).
 * - Null values are coerced to 0.
 */
export function columnarToGridData(
  data: Record<string, (number | null)[][]>,
  metrics: string[]
): GridData {
  // Determine grid dimensions from the first available column.
  const firstKey = Object.keys(data)[0];
  if (!firstKey) {
    return { ptd: [], ptg: [] };
  }
  const numTables = data[firstKey].length;
  const numRows = data[firstKey][0]?.length ?? 0;

  // Initialise empty MetricValues for every cell.
  const ptd: MetricValues[][] = Array.from({ length: numTables }, () =>
    Array.from({ length: numRows }, () => ({}))
  );
  const ptg: MetricValues[][] = Array.from({ length: numTables }, () =>
    Array.from({ length: numRows }, () => ({}))
  );

  for (const metric of metrics) {
    for (const agg of ["PTD", "PTG"] as const) {
      const key = buildColumnId(metric, agg);
      const col = data[key];
      if (!col) continue;

      const target = agg === "PTD" ? ptd : ptg;
      for (let ti = 0; ti < numTables; ti++) {
        for (let ri = 0; ri < numRows; ri++) {
          target[ti][ri][metric] = col[ti]?.[ri] ?? 0;
        }
      }
    }
  }

  return { ptd, ptg };
}

/**
 * Builds a cell-view function over the server columns: numeric server values
 * for one cell (nulls skipped), overlaid with the client-owned irreducibles.
 */
function makeCellView(
  serverColumns: Record<string, (number | null)[][]>
): (
  agg: TemporalAgg,
  irr: MetricValues,
  ti: number,
  ri: number
) => MetricValues {
  const byAgg: Record<TemporalAgg, [string, (number | null)[][]][]> = {
    PTD: [],
    PTG: [],
    TTL: []
  };
  for (const [key, col] of Object.entries(serverColumns)) {
    const { metric, agg } = parseColumnId(key);
    byAgg[agg].push([metric, col]);
  }
  return (agg, irr, ti, ri) => {
    const view: MetricValues = {};
    for (const [metric, col] of byAgg[agg]) {
      const v = col[ti]?.[ri];
      if (v != null) view[metric] = v;
    }
    return { ...view, ...irr };
  };
}

/** Optional inputs for {@link renderColumnar}. */
interface RenderColumnarOptions {
  /** Endpoint ("last") metrics whose TTL is the end-of-period (PTG) value. */
  endpointMetrics?: string[];
  /** Whether PTD is forecast (future year) rather than historical actuals. */
  ptdMutable?: boolean;
  /**
   * The padded server payload, nulls intact — the immutable base layer.
   * Null means "masked / not applicable" (e.g. the backend's inventory mask)
   * and must stay blank; absent columns simply don't render.
   */
  serverColumns?: Record<string, (number | null)[][]>;
  /**
   * Metrics an unsaved edit has invalidated: dropped from the output entirely,
   * so they render blank until the next server response. Blanked at the output
   * layer rather than by withholding inputs, or a ratio over the missing values
   * would render 0 instead of blank.
   */
  invalidatedMetrics?: string[];
}

/** The client-computed values for one cell, per temporal slice. */
export interface CellSlices {
  ptdValues: MetricValues;
  ptgValues: MetricValues;
  ttlValues: MetricValues;
}

/**
 * Builds the per-cell renderer: irreducibles in, the three ownership-gated
 * slices out. Shared by {@link renderColumnar} (every cell) and
 * {@link renderCellSlices} (a single cell, for the edit path).
 */
function makeCellRenderer(
  irreducibleMetrics: string[],
  kpiFlows: KpiFlow[],
  derivedMetrics: DerivedMetricDef[],
  options: RenderColumnarOptions
): (
  irrPtd: MetricValues,
  irrPtg: MetricValues,
  ti: number,
  ri: number
) => CellSlices {
  const {
    endpointMetrics = [],
    ptdMutable = false,
    serverColumns = {}
  } = options;
  const nonStockDerivations = derivedMetrics.filter(
    (d) => !d.stockBalanceDerived
  );
  const cellView = makeCellView(serverColumns);

  /** Server value for one cell; null when the column or cell is absent. */
  function serverValue(
    metric: string,
    agg: TemporalAgg,
    ti: number,
    ri: number
  ) {
    return serverColumns[buildColumnId(metric, agg)]?.[ti]?.[ri] ?? null;
  }

  // Whether a cell has any stock anchor to derive from. The primary (units)
  // flow stands in for all families — the backend masks them together.
  const primary = kpiFlows[0];
  function stockAnchored(ti: number, ri: number): boolean {
    if (!primary) return false;
    return [
      serverValue(primary.bop, "PTD", ti, ri),
      serverValue(primary.eop, "PTD", ti, ri),
      serverValue(primary.bop, "PTG", ti, ri),
      serverValue(primary.eop, "PTG", ti, ri)
    ].some((v) => v != null);
  }

  return function renderCell(irrPtd, irrPtg, ti, ri): CellSlices {
    const anchored = stockAnchored(ti, ri);

    // PTD first, so its closing stock is final before it seeds PTG's opening.
    const stockPtd = ptdMutable && anchored;
    const ptdValues = recalcDerivedMetrics(
      cellView("PTD", irrPtd, ti, ri),
      stockPtd ? derivedMetrics : nonStockDerivations
    );

    // Stock continuity: the forecast period opens at the actuals' closing
    // stock, so BOP|PTG = EOP|PTD (derived when PTD is mutable, the server's
    // point-in-time value otherwise). Falls back to the delivered BOP|PTG —
    // the backend only rolls a per-week BOP on some paths.
    const seededPtg = cellView("PTG", irrPtg, ti, ri);
    for (const flow of kpiFlows) {
      const anchor = stockPtd
        ? ptdValues[flow.eop]
        : serverValue(flow.eop, "PTD", ti, ri);
      if (anchor != null) seededPtg[flow.bop] = anchor;
    }
    const ptgValues = recalcDerivedMetrics(
      seededPtg,
      anchored ? derivedMetrics : nonStockDerivations
    );

    // TTL from the computed slices so its EOP follows the corrected PTG.
    // Stock endpoint rules only apply when the cell has stock at all.
    const ttlValues = recalcDerivedMetrics(
      deriveTtlFromPtdPtg(
        ptdValues,
        ptgValues,
        irreducibleMetrics,
        anchored ? kpiFlows : [],
        endpointMetrics
      ),
      derivedMetrics,
      true
    );

    return { ptdValues, ptgValues, ttlValues };
  };
}

/**
 * Renders the three slices of a single cell from grid state + server payload.
 *
 * Used by the edit path to build the KPI cascade's starting view from the
 * *current* grid state inside the `setGridData` updater — batched edits (e.g.
 * an AG Grid range paste) see each other's effects rather than a stale render
 * snapshot.
 */
export function renderCellSlices(
  gridData: GridData,
  tableIndex: number,
  rowIndex: number,
  config: EditConfig,
  serverColumns: Record<string, (number | null)[][]>
): CellSlices {
  const renderCell = makeCellRenderer(
    config.irreducibleMetrics,
    config.kpiFlows,
    config.derivedMetrics,
    {
      endpointMetrics: config.endpointMetrics,
      ptdMutable: config.ptdMutable,
      serverColumns
    }
  );
  return renderCell(
    gridData.ptd[tableIndex][rowIndex],
    gridData.ptg[tableIndex][rowIndex],
    tableIndex,
    rowIndex
  );
}

/**
 * Renders the columnar data for MultiTableGrid from the two ownership layers:
 *
 * - **Server-owned base**: `serverColumns` (the padded response) is copied
 *   verbatim, nulls intact. Anything the client doesn't compute below —
 *   pass-through metrics, masked cells, historical stock anchors — shows the
 *   server's value.
 * - **Client overlay**: per cell, the irreducible metrics from `gridData`
 *   (the only mutable state) plus everything derivable from them.
 *
 * Derivations run on a per-cell view of numeric server values overlaid with
 * the irreducibles. The stock-balance derivations (EOP/OTB from
 * `BOP + INTAKE − SALES`) are ownership-gated:
 *
 * - **Historical PTD** (`!ptdMutable`): stock is a measured fact — server-owned,
 *   never derived (received intake is folded into stock and reported as 0, so
 *   the balance would undercount).
 * - **Forecast slices** (PTG, and PTD when `ptdMutable`): derived live so edits
 *   move EOP immediately, opening from the anchor — EOP|PTD for PTG (stock
 *   continuity), the server's BOP for a mutable PTD.
 * - **No anchor** (inventory-masked rows, or stockless payloads): nothing is
 *   derived, so the server's nulls (or nothing) show. The gate is per cell,
 *   not per flow — the backend masks all stock families together.
 */
export function renderColumnar(
  gridData: GridData,
  irreducibleMetrics: string[],
  kpiFlows: KpiFlow[],
  derivedMetrics: DerivedMetricDef[],
  options: RenderColumnarOptions = {}
): Record<string, (number | null)[][]> {
  const { serverColumns = {}, invalidatedMetrics = [] } = options;
  const { ptd, ptg } = gridData;
  const numTables = ptd.length;
  if (numTables === 0) return {};
  const numRows = ptd[0].length;

  // Base layer: the server payload as-is.
  const result: Record<string, (number | null)[][]> = {};
  for (const [key, col] of Object.entries(serverColumns)) {
    result[key] = col.map((t) => [...t]);
  }

  /** Ensure a column exists in the result with the right dimensions. */
  function ensureColumn(key: string): (number | null)[][] {
    if (!result[key]) {
      result[key] = Array.from({ length: numTables }, () =>
        Array.from<number | null>({ length: numRows }).fill(null)
      );
    }
    return result[key];
  }

  const renderCell = makeCellRenderer(
    irreducibleMetrics,
    kpiFlows,
    derivedMetrics,
    options
  );

  for (let ti = 0; ti < numTables; ti++) {
    for (let ri = 0; ri < numRows; ri++) {
      const { ptdValues, ptgValues, ttlValues } = renderCell(
        ptd[ti][ri],
        ptg[ti][ri],
        ti,
        ri
      );

      const slices: [TemporalAgg, MetricValues][] = [
        ["PTD", ptdValues],
        ["PTG", ptgValues],
        ["TTL", ttlValues]
      ];

      for (const [agg, values] of slices) {
        for (const metric of Object.keys(values)) {
          const key = buildColumnId(metric, agg);
          ensureColumn(key)[ti][ri] = values[metric];
        }
      }
    }
  }

  for (const metric of invalidatedMetrics) {
    for (const agg of ["PTD", "PTG", "TTL"] as TemporalAgg[]) {
      delete result[buildColumnId(metric, agg)];
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Per-table row padding / filtering
// ---------------------------------------------------------------------------

/**
 * Pads per-table columnar data to rectangular form so the edit propagation
 * engine can work with it. Creates a union of all row labels across tables,
 * filling missing rows with null. Returns the padded data, the unified row
 * labels, and a membership map for filtering on render.
 *
 * The unified row labels have TTL last.
 */
export function padToRectangular(
  data: Record<string, (number | null)[][]>,
  tableLabels: string[],
  rowLabelsPerTable: string[][]
): {
  /** Padded columnar data — all tables have the same row count. */
  paddedData: Record<string, (number | null)[][]>;
  /** Unified row labels (TTL last). */
  unifiedRowLabels: string[];
  /** Per-table set of real (non-padding) row labels. */
  realRowsPerTable: Set<string>[];
} {
  // Build union of all row labels, TTL last.
  const allRows = new Set<string>();
  for (const labels of rowLabelsPerTable) {
    for (const label of labels) {
      allRows.add(label);
    }
  }
  const unifiedRowLabels = [...allRows].filter((r) => r !== "TTL").sort();
  if (allRows.has("TTL")) unifiedRowLabels.push("TTL");

  // Build per-table row index maps.
  const realRowsPerTable = rowLabelsPerTable.map((labels) => new Set(labels));

  // Pad each column.
  const paddedData: Record<string, (number | null)[][]> = {};
  for (const [col, tables] of Object.entries(data)) {
    paddedData[col] = tableLabels.map((_, ti) => {
      const originalLabels = rowLabelsPerTable[ti] ?? [];
      return unifiedRowLabels.map((rowLabel) => {
        const origIdx = originalLabels.indexOf(rowLabel);
        if (origIdx === -1) return null; // padding
        return tables[ti]?.[origIdx] ?? null;
      });
    });
  }

  return { paddedData, unifiedRowLabels, realRowsPerTable };
}

/**
 * Filters padded rectangular columnar data back to per-table rows for
 * rendering. Only includes rows that actually belong to each table.
 *
 * Returns per-table row labels and filtered columnar data.
 */
export function filterToPerTableRows(
  data: Record<string, (number | null)[][]>,
  tableLabels: string[],
  unifiedRowLabels: string[],
  realRowsPerTable: Set<string>[]
): {
  filteredData: Record<string, (number | null)[][]>;
  filteredRowLabelsPerTable: string[][];
} {
  // Build per-table filtered row indices.
  const filteredIndicesPerTable = tableLabels.map((_, ti) => {
    const realRows = realRowsPerTable[ti] ?? new Set();
    return unifiedRowLabels
      .map((label, idx) => (realRows.has(label) ? idx : -1))
      .filter((idx) => idx !== -1);
  });

  const filteredRowLabelsPerTable = filteredIndicesPerTable.map((indices) =>
    indices.map((idx) => unifiedRowLabels[idx])
  );

  const filteredData: Record<string, (number | null)[][]> = {};
  for (const [col, tables] of Object.entries(data)) {
    filteredData[col] = tableLabels.map((_, ti) => {
      const indices = filteredIndicesPerTable[ti];
      return indices.map((idx) => tables[ti]?.[idx] ?? null);
    });
  }

  return { filteredData, filteredRowLabelsPerTable };
}
